import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StatusPreautorisation } from '../../modeles/variables';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { Observable, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DialogSimpleTextContent } from '../dialog-simple/dialog-simple.component';
import { ToolPreautorisationService } from '../../../tool-preautorisation/tool-preautorisation.service';
import { PreautorisationModel } from 'src/app/tool-preautorisation/experimente/models/preautorisation.model';

export enum ResultatAnnulerRendezvous {
  Oui,
  Non
}

enum TranslationKeyEnum {
  ANNULER_PREAUT = 'preautorisation-en-ligne.modale-annuler-preautorisation.annuler-preautorisation',
  ANNULER_PREAUT_ACCEPTEE = 'preautorisation-en-ligne.modale-annuler-preautorisation.annuler-preautorisation-acceptee',
  ANNULER_PREAUT_ACCEPTEE_COEMP = 'preautorisation-en-ligne.modale-annuler-preautorisation.annuler-preautorisation-acceptee-coemprunteur',
  ANNULER_PREAUT_ATTENTE_COEMP = 'preautorisation-en-ligne.modale-annuler-preautorisation.annuler-preautorisation-en-attente-coemprunteur',
  TEXTE_ANNULER = 'preautorisation-en-ligne.modale-annuler-preautorisation.annuler-texte',
  PREAUT_ANNULEE = 'preautorisation-en-ligne.modale-annuler-preautorisation.preautorisation-annulee',
  DEMANDE_ANNULEE = 'preautorisation-en-ligne.modale-annuler-preautorisation.demande-annulee'
}

export interface DialogAnnulationTextContent extends DialogSimpleTextContent {
  confirmation?: string;
}

@Component({
  selector: 'app-dialog-annulation',
  templateUrl: './dialog-annulation.component.html',
  styleUrls: ['./dialog-annulation.component.scss'],
  standalone: false
})
export class DialogAnnulationComponent implements OnInit, OnDestroy {
  resultatAnnulerRendezvous = ResultatAnnulerRendezvous;

  private isCancelInProgress = false;
  isPreautEnLigneCanceled = false;

  preautorisationEnLigne$: Observable<PreautorisationModel>;
  preautorisationEnLigne: PreautorisationModel;
  preautorisationEnLigneSubscription: Subscription;

  constructor(
    public dialogRef: MatDialogRef<DialogAnnulationComponent, ResultatAnnulerRendezvous>,
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      isPreautEnLigne: boolean;
      scrollToTop?: boolean;
      hideConfirmation?: boolean;
      returnUrl?: string;
    },
    private readonly _router: Router,
    private readonly _toolPreautorisationService: ToolPreautorisationService
  ) {}

  ngOnInit() {
    disableBodyScroll();
    this.preautorisationEnLigne$ = this._toolPreautorisationService.getPreautorisation$;
    this.preautorisationEnLigneSubscription = this.preautorisationEnLigne$.subscribe(preautorisationEnLigne => {
      this.preautorisationEnLigne = preautorisationEnLigne;
    });
  }

  ngOnDestroy() {
    clearAllBodyScrollLocks();
    if (this.preautorisationEnLigneSubscription) {
      this.preautorisationEnLigneSubscription.unsubscribe();
    }
    if (this.isPreautEnLigneCanceled && this.data.scrollToTop && window) {
      // Empêche le scroll vers le bas quand la modale se ferme et qu'il y a eu une navigation
      window.scrollTo(0, 0);
    }
  }

  get isPreautEnLigne(): boolean {
    return this.data && this.data.isPreautEnLigne;
  }

  get hideConfirmation(): boolean {
    return this.data && this.data.hideConfirmation;
  }

  closeModal() {
    this.dialogRef.close();
  }

  async confirmCancel() {
    this.preautorisationEnLigneSubscription.unsubscribe();

    if (
      this.isPreautEnLigne &&
      this.preautorisationEnLigne &&
      this.preautorisationEnLigne.statut &&
      !this.hideConfirmation
    ) {
      if (!this.isCancelInProgress) {
        this.isCancelInProgress = true;
        await this._toolPreautorisationService.annulerPreautorisation();
        this.isPreautEnLigneCanceled = true;

        if (this.data && (this.data.returnUrl || this.data.returnUrl === '')) {
          this._router.navigate([this.data.returnUrl]);
        }
      }
    } else {
      this.dialogRef.close(ResultatAnnulerRendezvous.Oui);
    }
  }

  get textContent(): DialogAnnulationTextContent {
    return { ...this.messageTextContent, confirmButton: 'btn.oui', cancelButton: 'btn.non' };
  }

  private get messageTextContent(): DialogAnnulationTextContent {
    if (this.isPreautEnLigne && this.preautorisationEnLigne) {
      switch (this.preautorisationEnLigne.statut) {
        case StatusPreautorisation.ContreOffreAcceptee:
        case StatusPreautorisation.Acceptee:
          if (this.preautorisationEnLigne.indCoemprunteur) {
            return {
              // Acceptée - coemprunteur
              title: TranslationKeyEnum.ANNULER_PREAUT_ACCEPTEE,
              paragraph: TranslationKeyEnum.ANNULER_PREAUT_ACCEPTEE_COEMP,
              confirmation: TranslationKeyEnum.PREAUT_ANNULEE
            };
          } else {
            return {
              // Acceptée - seul
              title: TranslationKeyEnum.ANNULER_PREAUT_ACCEPTEE,
              confirmation: TranslationKeyEnum.PREAUT_ANNULEE
            };
          }
        case StatusPreautorisation.EnAttenteCoemprunteur:
          if (this.preautorisationEnLigne.indCoemprunteurPrincipal) {
            return {
              // En attente coemprunteur - coemprunteur 1
              title: TranslationKeyEnum.ANNULER_PREAUT,
              paragraph: TranslationKeyEnum.ANNULER_PREAUT_ATTENTE_COEMP,
              confirmation: TranslationKeyEnum.DEMANDE_ANNULEE
            };
          } else {
            return {
              // En attente coemprunteur - coemprunteur 2
              title: TranslationKeyEnum.ANNULER_PREAUT,
              paragraph: TranslationKeyEnum.TEXTE_ANNULER
            };
          }
        case StatusPreautorisation.RefuseeSimplifie:
        case StatusPreautorisation.ContreOffreEnAttente:
        case StatusPreautorisation.ContreOffreRefusee:
        case StatusPreautorisation.EnAttente:
        case StatusPreautorisation.EnAttenteSelectionTaux:
          if (this.preautorisationEnLigne.indCoemprunteur) {
            return {
              // En attente - coemprunteur
              title: TranslationKeyEnum.ANNULER_PREAUT,
              paragraph: TranslationKeyEnum.ANNULER_PREAUT_ATTENTE_COEMP,
              confirmation: TranslationKeyEnum.DEMANDE_ANNULEE
            };
          } else {
            return {
              // En attente - seul
              title: TranslationKeyEnum.ANNULER_PREAUT,
              paragraph: TranslationKeyEnum.TEXTE_ANNULER,
              confirmation: TranslationKeyEnum.DEMANDE_ANNULEE
            };
          }
        default:
          // En cours
          return {
            title: TranslationKeyEnum.ANNULER_PREAUT,
            paragraph: TranslationKeyEnum.TEXTE_ANNULER
          };
      }
    } else {
      // Rendez-vous
      return {
        title: 'preautorisation-en-ligne.modale-annuler-preautorisation.titre-annuler-rendez-vous',
        paragraph: 'preautorisation-en-ligne.modale-annuler-preautorisation.texte-annuler-rendez-vous'
      };
    }
  }
}
